import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, PlusCircle, FilterIcon } from 'lucide-react';
import ConfigureFunctionalitiesModal from './components/ConfigureFunctionalitiesModal';
import ConfigureIndicatorsModal from './components/ConfigureIndicatorsModal';
import AppliedFilters from '@/components/AppliedFilters';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import DynamicFormModal from '@/components/DynamicFormModal';
import { useToast } from '@/components/ui/use-toast';
import { laundriesColumns, laundriesFormColumns, columnsExcel } from '@/pages/parametrizacion/laundries/utils/laundriesColumns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fetchLaundries, createLaundry, updateLaundry, deleteLaundry, exportLaundries } from '@/pages/parametrizacion/laundries/Services/laundries.services';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';

const ITEMS_PER_PAGE = 10;
const DEFAULT_FILTERS = { estado: 'Activo' };

const LaundriesPage = () => {
  const { toast } = useToast();
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [modalMode, setModalMode] = useState('create');

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [isConfigureModalOpen, setIsConfigureModalOpen] = useState(false);
  const [itemToConfigure, setItemToConfigure] = useState(null);

  const [isIndicatorsModalOpen, setIsIndicatorsModalOpen] = useState(false);
  const [itemToConfigureIndicators, setItemToConfigureIndicators] = useState(null);

  const fetchAndSetLaundries = useCallback(async () => {
    setIsLoading(true);
    try {
      const offset = currentPage;
      const { data, totalRecords } = await fetchLaundries({ limit: ITEMS_PER_PAGE, offset, query: filters });
      setDisplayedData(data);
      setTotalItems(totalRecords);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'No se pudieron obtener los centros de lavado.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, filters, toast]);

  useEffect(() => {
    fetchAndSetLaundries();
  }, [fetchAndSetLaundries]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleOpenModal = (mode, item = null) => {
    setModalMode(mode);
    setCurrentItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentItem(null);
  };

  const handleSaveItem = async (itemData) => {
    try {
      if (modalMode === 'create') {
        const response = await createLaundry(itemData);
        if (response.code === 201) {
          toast({ title: "Centro de lavado creado", description: "El centro de lavado fue registrado correctamente." });
        } else {
          throw new Error(response.message || "Error al crear centro de lavado");
        }
      } else if (modalMode === 'edit') {
        const response = await updateLaundry(currentItem._id, itemData);
        if (response.code === 200) {
          toast({ title: "Centro de lavado actualizado", description: "Los datos fueron actualizados correctamente." });
        } else {
          throw new Error(response.message || "Error al actualizar centro de lavado");
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Ocurrió un problema al guardar el centro de lavado.",
        variant: "destructive",
      });
    } finally {
      handleCloseModal();
      fetchAndSetLaundries();
    }
  };

  const handleDeleteConfirmation = (item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteItem = async () => {
    try {
      const response = await deleteLaundry(itemToDelete._id);
      if (response.code === 200) {
        toast({ title: "Centro de lavado eliminado", description: "El registro fue eliminado correctamente." });
        fetchAndSetLaundries();
      } else {
        throw new Error(response.message || "Error al eliminar el centro de lavado");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Ocurrió un problema al eliminar el centro de lavado.",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleAction = (actionType, row) => {
    if (actionType === 'edit') {
      handleOpenModal('edit', row);
    } else if (actionType === 'delete') {
      handleDeleteConfirmation(row);
    } else if (actionType === 'configure') {
      setItemToConfigure(row);
      setIsConfigureModalOpen(true);
    } else if (actionType === 'indicators') {
      setItemToConfigureIndicators(row);
      setIsIndicatorsModalOpen(true);
    }
  };

  const handleSaveFunctionalities = async (functionalities) => {
    try {
      const response = await updateLaundry(itemToConfigure._id, {
        ...itemToConfigure,
        funcionalidadesHabilitadas: functionalities,
      });

      if (response.code === 200) {
        toast({
          title: "Funcionalidades configuradas",
          description: `Se configuraron ${functionalities.length} funcionalidades para ${itemToConfigure.nombre}.`
        });
        fetchAndSetLaundries();
      } else {
        throw new Error(response.message || "Error al configurar funcionalidades");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Ocurrió un problema al configurar las funcionalidades.",
        variant: "destructive",
      });
    } finally {
      setIsConfigureModalOpen(false);
      setItemToConfigure(null);
    }
  };

  const handleSaveIndicators = async (indicators) => {
    try {
      const response = await updateLaundry(itemToConfigureIndicators._id, {
        ...itemToConfigureIndicators,
        indicadores: indicators,
      });

      if (response.code === 200) {
        toast({
          title: "Indicadores configurados",
          description: `Se configuraron los indicadores para ${itemToConfigureIndicators.nombre}.`
        });
        fetchAndSetLaundries();
      } else {
        throw new Error(response.message || "Error al configurar indicadores");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Ocurrió un problema al configurar los indicadores.",
        variant: "destructive",
      });
    } finally {
      setIsIndicatorsModalOpen(false);
      setItemToConfigureIndicators(null);
    }
  };

  const handleFilterChange = (newFilters) => {
    setCurrentPage(1);
    setFilters(newFilters);
  };

  const handleRemoveFilter = (key) => {
    const newFilters = { ...filters };
    delete newFilters[key];
    setFilters(newFilters);
    setCurrentPage(1);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="p-4 md:p-6"
    >
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-primary/10 p-3 rounded-lg">
              <ShoppingCart className="h-8 w-8 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">Parametrización de Centros de lavado</CardTitle>
              <CardDescription>Gestiona los Centros de lavado de la empresa.</CardDescription>
            </div>
          </div>

          <div className="w-full md:w-auto flex flex-col md:flex-row gap-3">
            <div className="flex flex-wrap gap-2 justify-end">
              <ExportExcelButton
                title="Centros de lavado"
                columns={columnsExcel}
                getData={() => exportLaundries({ query: filters })}
              />
              <Button onClick={() => handleOpenModal('create')}>
                <PlusCircle className="mr-2 h-4 w-4" /> Crear Centro de lavado
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsDrawerOpen(true)}
                className="gap-2"
              >
                <FilterIcon className="mr-2 h-4 w-4" variant="outline" size={20} />
                Filtros
              </Button>
            </div>
          </div>

        </CardHeader>
        <CardContent>
          <div className="w-full">
            <AppliedFilters
              filters={filters}
              fields={[
                {
                  id: 'estado', label: 'Estado', type: 'select', options: [
                    { value: 'Activa', label: 'Activa' },
                    { value: 'Inactiva', label: 'Inactiva' },
                  ]
                },
              ]}
              onRemoveFilter={handleRemoveFilter}
            />
          </div>
          <DataTable
            data={displayedData}
            columns={laundriesColumns}
            isLoading={isLoading}
            onAction={handleAction}
            showCreateButton={false}
          />
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
          />
          <FilterDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            fields={[
              {
                id: 'estado', label: 'Estado', type: 'select', options: [
                  { value: 'Activa', label: 'Activa' },
                  { value: 'Inactiva', label: 'Inactiva' },
                ]
              },
            ]}
            initialFilters={filters}
            onChange={handleFilterChange}
            onApply={handleFilterChange}
            initialDay={true}
          />
        </CardContent>
      </Card>

      <AnimatePresence>
        {isModalOpen && (
          <DynamicFormModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onSave={handleSaveItem}
            item={currentItem}
            fields={laundriesFormColumns}
            title={modalMode === 'create' ? 'Crear Nuevo Centro de lavado' : 'Editar Centro de lavado'}
            mode={modalMode}
          />
        )}
      </AnimatePresence>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro de eliminar esta centro de lavado?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente el centro de lavado
              "{itemToDelete?.nombre}" de tus registros.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setItemToDelete(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteItem} className="bg-destructive hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AnimatePresence>
        {isConfigureModalOpen && (
          <ConfigureFunctionalitiesModal
            isOpen={isConfigureModalOpen}
            onClose={() => {
              setIsConfigureModalOpen(false);
              setItemToConfigure(null);
            }}
            onSave={handleSaveFunctionalities}
            laundry={itemToConfigure}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isIndicatorsModalOpen && (
          <ConfigureIndicatorsModal
            isOpen={isIndicatorsModalOpen}
            onClose={() => {
              setIsIndicatorsModalOpen(false);
              setItemToConfigureIndicators(null);
            }}
            onSave={handleSaveIndicators}
            laundry={itemToConfigureIndicators}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default LaundriesPage;
